import { relativeFromNow } from '@/lib/format'
import { Badge, EffortBadge, RatingStars } from '@/components/ui/Badge'
import { buttonClasses } from '@/components/ui/buttonStyles'
import { Card } from '@/components/ui/Card'
import { CuisineDot } from '@/components/ui/CuisineAccent'
import { MacroStrip } from '@/components/ui/MacroStrip'
import { summaryMacros } from '@/lib/macros'
import { PreviewButton } from '@/components/ui/PreviewButton'
import { RecipeCover } from '@/components/ui/RecipeCover'
import type { RecipeSummary } from '@/api/types'
import { Pencil } from 'lucide-react'
import { Link } from 'react-router-dom'

/** Card grid entry: cover, identity, rating, macros, and when it was last made. */
export function RecipeCard({
  recipe,
  onPreview,
}: {
  recipe: RecipeSummary
  onPreview: (id: number) => void
}) {
  const identity = [recipe.cuisine, recipe.protein_base].filter(Boolean).join(' · ')

  return (
    <Card interactive className="relative flex h-full flex-col overflow-hidden">
      <RecipeCover
        imageUrl={recipe.image_url}
        cuisine={recipe.cuisine}
        className="h-28 w-full"
      />
      <div className="absolute top-2.5 left-2.5 flex gap-1">
        {recipe.vegetarian ? (
          <Badge tone="positive" className="shadow-card">
            Veg
          </Badge>
        ) : null}
        {recipe.excluded ? (
          <Badge tone="critical" className="shadow-card">
            Excluded
          </Badge>
        ) : null}
      </div>

      <div className="flex flex-1 flex-col gap-2.5 p-4">
        <div className="space-y-1">
          {/* Always two lines tall so a long name cannot change the card height. */}
          <h3 className="line-clamp-2 min-h-[2lh] text-[0.9375rem] leading-snug font-semibold text-balance text-ink">
            {/* Stretched link: the whole card opens the recipe; the actions
                below opt back out so they stay clickable. */}
            <Link
              to={`/recipes/${recipe.id}`}
              className="before:absolute before:inset-0 hover:text-accent"
            >
              {recipe.name}
            </Link>
          </h3>
          <p className="flex items-center gap-1.5 text-xs text-ink-soft">
            <CuisineDot cuisine={recipe.cuisine} />
            <span className="truncate">{identity || 'No cuisine set'}</span>
          </p>
        </div>

        <div className="flex items-center gap-3">
          <RatingStars value={recipe.rating} />
          <EffortBadge level={recipe.effort_level} />
        </div>

        <MacroStrip macros={summaryMacros(recipe)} />

        <div className="mt-auto flex items-center justify-between gap-2 border-t border-line pt-2.5">
          <span className="truncate text-xs text-ink-faint">
            {recipe.last_prep ? `Made ${relativeFromNow(recipe.last_prep)}` : 'Never made'}
          </span>
          <div className="relative z-10 flex shrink-0 items-center gap-0.5">
            <PreviewButton iconOnly onClick={() => onPreview(recipe.id)} />
            <Link
              to={`/recipes/${recipe.id}/edit`}
              aria-label="Edit"
              title="Edit"
              className={buttonClasses('ghost', 'icon-sm', 'text-ink-faint')}
            >
              <Pencil size={14} aria-hidden />
            </Link>
          </div>
        </div>
      </div>
    </Card>
  )
}
